/**
 * Waits used when a caller has no opinion of its own. Four waits means five
 * attempts, spread over about half a minute — long enough to outlast a
 * service restart or a dropped connection, short enough that someone is not
 * left staring at a dead page when the server really has gone.
 */
export const DEFAULT_BACKOFF_MS: readonly number[] = [1_000, 3_000, 8_000, 20_000];

/** What the loop should do with a failure it has just been handed. */
export enum FailureVerdict {
    Retry = 'retry',
    Stop = 'stop',
}

/**
 * Retry policy for {@link retryWithBackoff}.
 *
 * There is no separate attempt count: one backoff entry per retry means the
 * two can never disagree about how many attempts a policy describes.
 */
export type RetryPolicy = {
    /** How long to wait before each retry, in order. Omit for
     *  {@link DEFAULT_BACKOFF_MS}; pass an empty list to try once and hand the
     *  failure straight back, which is a different thing from having no
     *  opinion about the waits. */
    backoffMs?: readonly number[];
    /** Consulted on each failure the loop could still retry, with the number
     *  of the attempt that produced it. Answer {@link FailureVerdict.Stop} for
     *  a failure that going round again cannot help — a refusal rather than a hiccup — and
     *  the error is rethrown at once. Defaults to retrying.
     *
     *  This is also where a caller logs, since it sees every failure it has a
     *  say over. It is not consulted once the waits are spent: there is
     *  nothing left to decide, and the caller is about to receive the error
     *  anyway. */
    onFailure?: (error: unknown, attempt: number) => FailureVerdict;
};

const pause = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Run an operation, retrying failures that look temporary.
 *
 * Resolves with the first successful attempt. Throws the failure that ended
 * it: either one `onFailure` called a halt to, or the last attempt's, once
 * the backoffs are spent. The caller decides what a final failure means —
 * knowing that is rarely the retrying code's business.
 */
export const retryWithBackoff = async <T>(
    operation: () => Promise<T>,
    { backoffMs = DEFAULT_BACKOFF_MS, onFailure }: RetryPolicy = {}
): Promise<T> => {
    // Each attempt is handed the waits still available to it. On a failure it
    // sleeps through the head and passes the tail on; running out of waits is
    // what ends the recursion, so there is no terminating condition to keep in
    // step with the list. Which attempt this is falls out of how much of the
    // list is left, rather than being carried alongside it.
    const attemptWith = async (remainingWaits: readonly number[]): Promise<T> => {
        try {
            return await operation();
        } catch (error) {
            const [wait, ...waitsAfterThisOne] = remainingWaits;
            const attempt = backoffMs.length - remainingWaits.length + 1;

            if (wait === undefined || onFailure?.(error, attempt) === FailureVerdict.Stop) {
                throw error;
            }

            await pause(wait);
            return attemptWith(waitsAfterThisOne);
        }
    };

    return attemptWith(backoffMs);
};
